// Each avatar pairs an indicator surface with its `text-body-on{hue}` foreground,
// which the design system flips (white/black) per theme to stay legible on that
// surface. The palette is limited to the indicator hues whose on-color clears WCAG
// AA (4.5:1) in BOTH themes. `primary`/`success` are excluded: their on-color is
// white on the (rebrand-brightened) blue-500/green-600 and fails in light mode —
// giving the indicator family its own per-surface on-color token is design-owned
// (FF-196).
export const AVATAR_COLORS = [
  { bg: "bg-bg-indicator-secondary", text: "text-text-body-onSecondary" },
  { bg: "bg-bg-indicator-punch", text: "text-text-body-onPunch" },
  { bg: "bg-bg-indicator-soft", text: "text-text-body-onSoft" },
  { bg: "bg-bg-indicator-danger", text: "text-text-body-onDanger" },
] as const;

/**
 * Deterministically maps an identifier to one of the avatar background/foreground
 * color pairs. A simple hash keeps the same identifier on the same colors.
 */
export const getAvatarColorClasses = (identifier: string): (typeof AVATAR_COLORS)[number] => {
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = (hash * 31 + identifier.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/**
 * Extracts up to 2 uppercase initials from a name string.
 *
 * - "John Doe" → "JD"
 * - "John" → "JO"
 * - "John Michael Doe" → "JD" (first + last)
 * - "" / undefined → "?"
 */
export const getAvatarInitials = (name: string | undefined | null): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};
